import { api } from '../slices/api';

export type MovementAction =
	| 'received'
	| 'cut'
	| 'consumed'
	| 'scrapped'
	| 'remnant_returned'
	| 'job_returned'
	| 'split'
	| 'added'
	| 'moved'
	| 'status_changed'
	| 'length_adjusted'
	| 'deleted';

export interface MovementUser {
	id: number;
	name: string | null;
}

export interface MaterialMovementRow {
	id: string;
	action: MovementAction;
	action_label: string;
	batch_id: string;
	occurred_at: string;
	performed_by: MovementUser | null;
	recorded_by: MovementUser | null;
	material_length_id: string | null;
	bar_friendly_id: string;
	parent_bar_friendly_id: string | null;
	length_before: string | null;
	length_after: string | null;
	status_before: string | null;
	status_after: string | null;
	from_location: string | null;
	to_location: string | null;
	order_line_item: { id: string; name: string } | null;
	notes: string | null;
	is_backfilled: boolean;
}

export interface MovementBar {
	id: string | null;
	friendly_id: string;
	current_length: string | null;
	current_location: string | null;
	status: string | null;
	deleted: boolean;
}

export interface MovementReportBar extends MovementBar {
	movements: MaterialMovementRow[];
}

export interface MovementReportFamily {
	root_id: string | null;
	root_friendly_id: string;
	bars: MovementReportBar[];
}

export interface MovementReportLot {
	material_id: string | null;
	material_name: string;
	families: MovementReportFamily[];
}

export interface MovementActionSummary {
	action: MovementAction;
	label: string;
	transactions: number;
	bars: number;
}

export interface MovementOperatorRow {
	id: number | null;
	name: string;
	total: number;
	by_action: Partial<Record<MovementAction, number>>;
}

export interface MaterialMovementReport {
	date_from: string;
	date_to: string;
	start: string;
	end: string;
	timezone: string;
	summary: {
		by_action: MovementActionSummary[];
		transactions: number;
		bars_touched: number;
		operators: number;
	};
	operators: MovementOperatorRow[];
	groups: MovementReportLot[];
	row_count: number;
	truncated: boolean;
	row_cap: number;
}

export interface MaterialMovementReportParams {
	date_from: string;
	date_to: string;
	performed_by?: number[];
	actions?: MovementAction[];
	bar?: string;
	building_ids?: string[];
}

export interface MaterialLengthHistory {
	bar: MovementBar;
	material_name: string | null;
	family_root_friendly_id: string;
	include_family: boolean;
	movements: MaterialMovementRow[];
	truncated: boolean;
}

export const buildMovementReportQuery = (
	params: MaterialMovementReportParams
): string => {
	const query = new URLSearchParams({
		date_from: params.date_from,
		date_to: params.date_to,
	});
	if (params.performed_by?.length) {
		query.set('performed_by', params.performed_by.join(','));
	}
	if (params.actions?.length) query.set('actions', params.actions.join(','));
	if (params.bar?.trim()) query.set('bar', params.bar.trim());
	if (params.building_ids?.length) {
		query.set('building_ids', params.building_ids.join(','));
	}
	return query.toString();
};

export const materialMovementsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMaterialMovementReport: builder.query<
			MaterialMovementReport,
			MaterialMovementReportParams
		>({
			query: (params) =>
				`material/reports/material-movements/?${buildMovementReportQuery(params)}`,
		}),
		getMaterialLengthHistory: builder.query<
			MaterialLengthHistory,
			{ id: string; includeFamily?: boolean }
		>({
			query: ({ id, includeFamily }) =>
				`material/material-lengths/${id}/history/${includeFamily ? '?include_family=true' : ''}`,
		}),
	}),
});

export const {
	useGetMaterialMovementReportQuery,
	useGetMaterialLengthHistoryQuery,
} = materialMovementsApi;
